import { useEffect } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { usePrices } from "@/lib/hooks";
import { formatDollars } from "@/lib/formatters";
import type { CheckoutFormData } from "@/pages/CheckoutPage";

const MAX_EMPLOYEES = 50;

const PRODUCT_OPTIONS: {
  value: "bcb" | "badge_attested";
  label: string;
  price: (prices: { bcbPrice: number; badgePrice: number }) => number;
}[] = [
  {
    value: "bcb",
    label: "Full check + badge",
    price: (prices) => prices.bcbPrice,
  },
  {
    value: "badge_attested",
    label: "Already-cleared badge",
    price: (prices) => prices.badgePrice,
  },
];

export function EmployeeFields(): React.ReactElement {
  const {
    register,
    control,
    watch,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<CheckoutFormData>();

  // Dollar figures in the copy come from the configured prices, never from
  // literals, so a price change cannot strand stale words. Until they load, the
  // toggles carry no amounts and the helper and attestation copy are not shown.
  const { data: prices } = usePrices();
  const anyAttested = (watch("employees") ?? []).some(
    (employee) => employee.product === "badge_attested"
  );

  // Unmarking the last already-cleared row drops the attestation with it: the
  // requirement is gone, and marking a row again asks afresh. Lives here, with
  // the checkbox it resets, so every page that renders these rows gets it.
  useEffect(() => {
    if (anyAttested) return;
    if (getValues("attestationAccepted")) setValue("attestationAccepted", false);
    clearErrors("attestationAccepted");
  }, [anyAttested, getValues, setValue, clearErrors]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "employees",
  });

  const employeesErrors = errors.employees;
  const employeeRowErrors = Array.isArray(employeesErrors)
    ? employeesErrors
    : undefined;
  const employeesRootError =
    !Array.isArray(employeesErrors) && employeesErrors?.message
      ? employeesErrors.message
      : undefined;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Employees</h3>
      <p className="text-sm text-gray-600">
        Enter the first and last name of each employee who needs a background
        check and badge.
      </p>

      {prices && (
        <p className="text-sm text-gray-600">
          Not sure whether your employees are already cleared? Start with the{" "}
          {formatDollars(prices.badgePrice)} badge. If they are not found in the
          Clearinghouse, you pay the remaining{" "}
          {formatDollars(prices.bcbPrice - prices.badgePrice)}. Same total
          either way, and you risk nothing but time.
        </p>
      )}

      <div className="space-y-3">
        {fields.map((field, index) => {
          const rowError = employeeRowErrors?.[index];
          const firstNameError = rowError?.firstName?.message;
          const lastNameError = rowError?.lastName?.message;

          return (
            <div key={field.id} className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <div className="flex gap-3">
                  <div className="flex-1">
                    {index === 0 && (
                      <label
                        htmlFor={`employees.${index}.firstName`}
                        className="block text-sm font-medium text-gray-700"
                      >
                        First Name
                      </label>
                    )}
                    <input
                      id={`employees.${index}.firstName`}
                      type="text"
                      placeholder="First name"
                      {...register(`employees.${index}.firstName`)}
                      className={`${index === 0 ? "mt-1" : ""} block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        firstNameError
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {firstNameError && (
                      <p className="mt-1 text-xs text-red-600">
                        {firstNameError}
                      </p>
                    )}
                  </div>
                  <div className="flex-1">
                    {index === 0 && (
                      <label
                        htmlFor={`employees.${index}.lastName`}
                        className="block text-sm font-medium text-gray-700"
                      >
                        Last Name
                      </label>
                    )}
                    <input
                      id={`employees.${index}.lastName`}
                      type="text"
                      placeholder="Last name"
                      {...register(`employees.${index}.lastName`)}
                      className={`${index === 0 ? "mt-1" : ""} block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        lastNameError
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {lastNameError && (
                      <p className="mt-1 text-xs text-red-600">
                        {lastNameError}
                      </p>
                    )}
                  </div>
                </div>

                <fieldset className="flex flex-wrap gap-x-6 gap-y-1">
                  <legend className="sr-only">
                    Product for employee {index + 1}
                  </legend>
                  {PRODUCT_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="radio"
                        value={option.value}
                        {...register(`employees.${index}.product`)}
                        className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {option.label}
                      {prices
                        ? ` \u2014 ${formatDollars(option.price(prices))}`
                        : ""}
                    </label>
                  ))}
                </fieldset>
              </div>

              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className={`${index === 0 ? "mt-7" : ""} flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 text-gray-400 hover:border-red-300 hover:text-red-500`}
                  aria-label={`Remove employee ${index + 1}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Root-level employees array error (e.g. min/max) */}
      {employeesRootError && (
        <p className="text-xs text-red-600">{employeesRootError}</p>
      )}

      <button
        type="button"
        onClick={() => append({ firstName: "", lastName: "", product: "bcb" })}
        disabled={fields.length >= MAX_EMPLOYEES}
        className="rounded-md border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        + Add Employee
      </button>

      {/* One attestation, scoped to exactly the rows marked already-cleared. The
          server stamps copyVersion "2026-08-20"; this renders the words. */}
      {anyAttested && prices && (
        <div>
          <div className="flex items-start gap-2">
            <input
              id="attestationAccepted"
              type="checkbox"
              {...register("attestationAccepted")}
              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="attestationAccepted"
              className="text-sm text-gray-700"
            >
              I attest the employees marked as already cleared are in the
              Florida Clearinghouse. If one is not found, their badge is delayed
              and a full background check is required — that employee&apos;s{" "}
              {formatDollars(prices.badgePrice)} applies toward it and you pay
              the remaining{" "}
              <strong>
                {formatDollars(prices.bcbPrice - prices.badgePrice)}
              </strong>
              , the same {formatDollars(prices.bcbPrice)} total as ordering the
              full check up front.
            </label>
          </div>
          {errors.attestationAccepted?.message && (
            <p className="mt-1 text-xs text-red-600">
              {errors.attestationAccepted.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
